import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listSites, createSite, updateSite, deleteSite, listMyOrgUnits, type SiteSummary,
} from "../api/rest";
import { useSiteStore } from "../stores/siteStore";
import css from "./WarehouseAdminPage.module.css";

interface Form {
  id: string; name: string; timezone: string; orgUnitId: string;
  lat: string; lng: string;
}
const EMPTY: Form = { id: "", name: "", timezone: "America/Sao_Paulo", orgUnitId: "", lat: "", lng: "" };

export function SitesAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(EMPTY);

  const { data: sites = [] } = useQuery({ queryKey: ["sites"], queryFn: listSites });
  // Grant-scoped (ADR 177): a tenant admin sees only the tenant(s) they administer.
  const { data: tenants = [] } = useQuery({ queryKey: ["me-org-units"], queryFn: listMyOrgUnits });
  const tenantOptions = tenants.filter((tn) => tn.type === "tenant");
  // Effective tenant: the explicit pick, or the sole tenant the user administers
  // (auto-select for the tenant-admin case) — pure derivation, no render-phase setState.
  const orgUnitId = form.orgUnitId || (tenantOptions.length === 1 ? tenantOptions[0].id : "");

  const create = useMutation({
    mutationFn: (body: Parameters<typeof createSite>[0]) => createSite(body),
    onSuccess: () => {
      setForm(EMPTY);
      qc.invalidateQueries({ queryKey: ["sites"] });
      // Refresh the store so the shell (nav gating + setup checklist) sees the
      // new site immediately, without a reload (ADR 175).
      void useSiteStore.getState().loadAvailableSites();
    },
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Parameters<typeof updateSite>[1] }) => updateSite(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites"] });
      // Capability edits drive nav gating off the store's /me/sites snapshot, so refresh
      // it — the ["sites"] query only feeds this admin table, not the nav (ADR 120/183).
      void useSiteStore.getState().loadAvailableSites();
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => deleteSite(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sites"] });
      void useSiteStore.getState().loadAvailableSites();   // drop a deleted site from nav + picker
    },
  });

  function onCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!form.id.trim() || !form.name.trim() || !form.timezone.trim() || !orgUnitId) return;
    create.mutate({
      id: form.id.trim(),
      name: form.name.trim(),
      timezone: form.timezone.trim(),
      orgUnitId,
      lat: form.lat ? parseFloat(form.lat) : undefined,
      lng: form.lng ? parseFloat(form.lng) : undefined,
    });
  }

  return (
    <div className={css.root}>
      <div className={css.header}>
        <div className={css.title}>{t("nav.sites")}</div>
      </div>

      {tenantOptions.length === 0 && (
        <div className={css.notice}>
          {t("sitesAdmin.noTenants")}{" "}
          <a href="/admin/tenants">{t("sitesAdmin.noTenantsAction")}</a>
        </div>
      )}

      <form className={css.form} onSubmit={onCreate}>
        <div className={css.field}>
          <label className={css.label}>{t("sitesAdmin.tenant")}</label>
          <select className={css.input} value={orgUnitId}
                  onChange={(e) => setForm({ ...form, orgUnitId: e.target.value })}>
            <option value="">{t("sitesAdmin.tenantPlaceholder")}</option>
            {tenantOptions.map((ou) => <option key={ou.id} value={ou.id}>{ou.name}</option>)}
          </select>
        </div>
        <div className={css.field}>
          <label className={css.label}>{t("common.col.id")}</label>
          <input className={css.input} value={form.id}
                 onChange={(e) => setForm({ ...form, id: e.target.value })}
                 placeholder="site-br-spo-02" />
        </div>
        <div className={css.field}>
          <label className={css.label}>{t("common.col.name")}</label>
          <input className={css.input} value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className={css.field}>
          <label className={css.label}>{t("sitesAdmin.timezone")}</label>
          <input className={css.input} value={form.timezone}
                 onChange={(e) => setForm({ ...form, timezone: e.target.value })} />
        </div>
        <div className={css.field}>
          <label className={css.label}>Lat</label>
          <input className={css.input} value={form.lat} type="number" step="any"
                 onChange={(e) => setForm({ ...form, lat: e.target.value })} />
        </div>
        <div className={css.field}>
          <label className={css.label}>Lng</label>
          <input className={css.input} value={form.lng} type="number" step="any"
                 onChange={(e) => setForm({ ...form, lng: e.target.value })} />
        </div>
        <button type="submit" className={css.btn} disabled={create.isPending || !orgUnitId}>
          {create.isPending ? t("common.creating") : t("common.create")}
        </button>
      </form>

      <table className={css.table}>
        <thead>
          <tr><th>{t("common.col.id")}</th><th>{t("common.col.name")}</th><th>{t("sitesAdmin.col.tz")}</th><th>Lat</th><th>Lng</th><th>{t("sitesAdmin.col.capabilities")}</th><th>{t("common.col.actions")}</th></tr>
        </thead>
        <tbody>
          {sites.map((s) => <SiteRow key={s.id} site={s} onUpdate={(b) => update.mutate({ id: s.id, body: b })}
                                     onDelete={() => { if (confirm(t("warehousesAdmin.confirmDelete", { name: s.name }))) del.mutate(s.id); }} />)}
        </tbody>
      </table>
    </div>
  );
}

function SiteRow({ site, onUpdate, onDelete }: {
  site: SiteSummary;
  onUpdate: (body: Parameters<typeof updateSite>[1]) => void;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  return (
    <tr>
      <td>{site.id}</td>
      <td>{site.name}</td>
      <td>{site.timezone}</td>
      <td>{site.lat ?? "—"}</td>
      <td>{site.lng ?? "—"}</td>
      {/* Capability flags (ADR 139): these drive nav gating for non-admins and
          the capability-gated scene layers (occupancy), so they must be
          editable here — before this the only write path was SQL. */}
      <td>
        <label className={css.capToggle}>
          <input type="checkbox" checked={!!site.hasRtls}
                 onChange={(e) => onUpdate({ hasRtls: e.target.checked })} />
          {t("sitesAdmin.cap.rtls")}
        </label>
        <label className={css.capToggle}>
          <input type="checkbox" checked={!!site.hasTelemetry}
                 onChange={(e) => onUpdate({ hasTelemetry: e.target.checked })} />
          {t("sitesAdmin.cap.telemetry")}
        </label>
        <label className={css.capToggle}>
          <input type="checkbox" checked={!!site.hasInventory}
                 onChange={(e) => onUpdate({ hasInventory: e.target.checked })} />
          {t("sitesAdmin.cap.inventory")}
        </label>
      </td>
      <td className={css.actions}>
        <button className={css.linkBtn} onClick={() => {
          const newName = prompt(t("sitesAdmin.promptName"), site.name);
          if (newName && newName !== site.name) onUpdate({ name: newName });
        }}>{t("common.rename")}</button>
        <button className={css.dangerBtn} onClick={onDelete}>{t("common.delete")}</button>
      </td>
    </tr>
  );
}
